/**
 * Warden — Continuous Compliance practice.
 *
 * These are retained engagements delivered on TraceLock. They describe the work
 * Warden performs; the site makes no claim about response coverage either way.
 */

import type { ServicePageContent } from "./types";

const PRACTICE = "Continuous Compliance";

const MANAGED_COMPLIANCE: ServicePageContent = {
  path: "/warden/managed-compliance",
  practice: PRACTICE,
  title: "Managed Compliance",
  summary:
    "We run the control monitoring and evidence program so you do not have to hire for it.",
  headline: "A compliance program is an operation, and operations need people",
  lede:
    "A retained engagement where Warden runs your continuous control monitoring on TraceLock — chasing failing controls, keeping evidence current and preparing the audit.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=managed-compliance" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  scope: {
    heading: "Tooling does not close a failing control",
    body:
      "Continuous monitoring tells you a control has failed. Somebody still has to work out why, find the owner, get it fixed and record what happened — which is where understaffed programs quietly stop.",
    points: [
      {
        title: "Alert volume becomes background noise",
        body: "A monitoring platform nobody triages is an audit finding waiting to be written.",
      },
      {
        title: "Evidence goes stale between audits",
        body: "Screenshots gathered in audit season are the reason the next audit takes just as long.",
      },
      {
        title: "The work is spiky",
        body: "Hiring for audit-season peak is expensive; leaving it to whoever is free is how programs drift.",
      },
    ],
  },
  method: {
    heading: "How the retainer runs",
    steps: [
      {
        title: "Onboarding",
        body: "Connect the estate, adopt the control library, and establish the baseline of what is passing today.",
      },
      {
        title: "Weekly operation",
        body: "Triage control failures, drive remediation with named owners, and keep the evidence chain current.",
      },
      {
        title: "Monthly reporting",
        body: "Posture, exceptions and trend, in a form that goes straight to your risk committee.",
      },
      {
        title: "Audit support",
        body: "Populate the auditor room, respond to sample requests and manage the evidence exchange.",
      },
    ],
  },
  deliverables: {
    heading: "What the retainer includes",
    items: [
      "Continuous control monitoring operated on your behalf",
      "Triage and remediation follow-through on failing controls, with named owners",
      "Evidence kept current and cryptographically verifiable rather than collected in a rush",
      "Monthly posture reporting suitable for a risk committee",
      "Audit-season support: auditor room, sample handling and evidence exchange",
    ],
  },
  products: {
    heading: "What it runs on",
    slugs: ["tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Evidence Operations", href: "/warden/evidence-operations" },
      { label: "Auditor Room Management", href: "/warden/auditor-room" },
      { label: "Control Crosswalk Build-out", href: "/warden/crosswalk" },
    ],
  },
  close: {
    heading: "Buy the outcome, not the headcount",
    body:
      "For most organizations under fifty people, a retained program costs less than the compliance manager they were about to hire.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=managed-compliance" },
      { label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
    ],
  },
};

const EVIDENCE_OPERATIONS: ServicePageContent = {
  path: "/warden/evidence-operations",
  practice: PRACTICE,
  title: "Evidence Operations",
  summary:
    "Turn evidence collection from an audit-season project into a background process.",
  headline: "Collect evidence when it happens, not when the auditor asks",
  lede:
    "An engagement that replaces manual evidence gathering with automated collection into a write-once store, so the record is contemporaneous, attributable and verifiable without anyone taking a screenshot.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=evidence-ops" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  scope: {
    heading: "Retrospective evidence is weaker evidence",
    body:
      "A screenshot taken in March to demonstrate a control that operated in October proves the control's state in March. Auditors increasingly say so, and evidence produced after the fact invites sampling you would rather avoid.",
    points: [
      {
        title: "Screenshots have no provenance",
        body: "An image in a shared drive cannot show who produced it, when, or from what system state.",
      },
      {
        title: "Collection competes with real work",
        body: "The people who can produce the evidence are the people you least want spending a week producing it.",
      },
      {
        title: "The same evidence is gathered repeatedly",
        body: "SOC 2, ISO 27001 and a customer questionnaire ask for the same artifact in three formats.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Evidence mapping",
        body: "For each control, what would actually satisfy an auditor and which system already holds it.",
      },
      {
        title: "Automate collection",
        body: "Connectors pull the artifact on a schedule into write-once storage with a verifiable digest.",
      },
      {
        title: "Close the manual gaps",
        body: "For evidence no system produces, a lightweight attested workflow rather than a screenshot habit.",
      },
      {
        title: "Prove the chain",
        body: "Demonstrate integrity verification end to end so the store stands up to challenge.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "An evidence map: control, sufficient artifact, source system, collection frequency",
      "Automated collection running against your live estate",
      "A write-once evidence store with verifiable digests and retention aligned to your frameworks",
      "Attested workflows for the evidence that has no system of record",
      "An integrity verification walkthrough you can repeat in front of an auditor",
    ],
  },
  products: {
    heading: "What it runs on",
    slugs: ["tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
      { label: "Auditor Room Management", href: "/warden/auditor-room" },
    ],
  },
  close: {
    heading: "Evidence that was true when it was recorded",
    body:
      "Contemporaneous, attributable and verifiable. The three properties that stop an audit becoming an investigation.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=evidence-ops" },
      { label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
    ],
  },
};

const AUDITOR_ROOM: ServicePageContent = {
  path: "/warden/auditor-room",
  practice: PRACTICE,
  title: "Auditor Room Management",
  summary:
    "Run the audit exchange so your engineers answer questions once, not five times.",
  headline: "Audit cost is mostly the cost of interrupting the people who build things",
  lede:
    "Warden runs the auditor-facing side of your audit: a read-only evidence room, managed sample requests, and a single channel between the audit team and your organization.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=auditor-room" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  scope: {
    heading: "The expensive part of an audit is coordination",
    body:
      "Fieldwork itself is finite. What consumes the quarter is requests arriving by email to whoever the auditor met, chased individually, answered inconsistently and then asked again.",
    points: [
      {
        title: "Requests fan out uncontrolled",
        body: "Without one channel, the same question reaches three engineers and gets three different answers.",
      },
      {
        title: "Ad hoc sharing overexposes",
        body: "Evidence emailed as attachments leaves the boundary and cannot be withdrawn.",
      },
      {
        title: "Nobody tracks the state",
        body: "Halfway through fieldwork it is unclear what is outstanding and what is blocked on whom.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Room setup",
        body: "Scoped, read-only auditor access to exactly the evidence in scope, with access logged.",
      },
      {
        title: "Request intake",
        body: "One channel, tracked, with an owner and a due date per request.",
      },
      {
        title: "Response drafting",
        body: "Warden drafts from existing evidence and only escalates to your team when the answer genuinely requires them.",
      },
      {
        title: "Close-out",
        body: "A complete record of what was asked, what was provided and when, retained for the next audit.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "A read-only auditor room with per-artifact access logging",
      "A tracked request register with owners, status and dates",
      "Drafted responses grounded in evidence already held",
      "A close-out pack that becomes the starting point for the next cycle",
      "A measured record of engineering hours consumed, so the next audit can be planned",
    ],
  },
  products: {
    heading: "What it runs on",
    slugs: ["tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Evidence Operations", href: "/warden/evidence-operations" },
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
    ],
  },
  close: {
    heading: "Give the auditor a room, not your engineers",
    body:
      "The audit still happens. It just stops being the thing your platform team did that quarter.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=auditor-room" },
      { label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
    ],
  },
};

const CROSSWALK: ServicePageContent = {
  path: "/warden/crosswalk",
  practice: PRACTICE,
  title: "Control Crosswalk Build-out",
  summary:
    "Map one control set across every framework you are held to, so evidence is produced once.",
  headline: "Do the control once, and let it answer every framework that asks",
  lede:
    "An engagement that builds a unified control library for your organization and maps it across the frameworks you are held to, so a single piece of evidence satisfies each of them rather than being reproduced per audit.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=crosswalk" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  scope: {
    heading: "Framework-by-framework programs duplicate everything",
    body:
      "Run SOC 2, then ISO 27001, then an AI framework as separate projects and you build three control sets, three evidence collections and three sets of owners for what is substantially the same underlying practice.",
    points: [
      {
        title: "The overlap is large and unexploited",
        body: "Access control, change management and vendor management recur in nearly every framework with different wording.",
      },
      {
        title: "Divergence creates contradiction",
        body: "Separately maintained control descriptions eventually say different things about the same practice, which auditors notice.",
      },
      {
        title: "Adding a framework should be incremental",
        body: "With a mapped library, a new obligation is a delta; without one, it is another program.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Consolidate",
        body: "Reduce existing control sets to one library expressed in terms of what your organization actually does.",
      },
      {
        title: "Map",
        body: "Crosswalk each control to the requirements it satisfies across your frameworks, recording the rationale.",
      },
      {
        title: "Find the gaps",
        body: "Requirements no existing control covers, which is usually where the real work is.",
      },
      {
        title: "Wire to evidence",
        body: "Attach each control to its evidence source so the mapping produces artifacts, not just a matrix.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "A unified control library owned by your organization, not by a tool",
      "A crosswalk to each framework in scope, with rationale per mapping",
      "A gap list of requirements no current control satisfies",
      "Evidence sources attached per control",
      "A documented procedure for adding the next framework as a delta",
    ],
  },
  drivers: {
    heading: "Frameworks commonly in scope",
    items: [
      {
        title: "SOC 2 and ISO/IEC 27001",
        body: "The two most requested by enterprise buyers, with substantial overlap that is rarely exploited.",
      },
      {
        title: "ISO/IEC 42001 and NIST AI RMF",
        body: "The AI layer, which reuses much of the existing security control set plus AI-specific additions.",
      },
      {
        title: "Sector obligations",
        body: "HIPAA, PCI DSS, NERC CIP, CMMC and NIST SP 800-171 as applicable, mapped into the same library.",
      },
    ],
  },
  products: {
    heading: "What it runs on",
    slugs: ["tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "ISO 42001 Alignment", href: "/warden/iso-42001" },
      { label: "Evidence Operations", href: "/warden/evidence-operations" },
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
    ],
  },
  close: {
    heading: "One library, many auditors",
    body:
      "The saving compounds with every framework you add, which is why this is worth doing before the next one arrives.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=crosswalk" },
      { label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
    ],
  },
};

export const COMPLIANCE_SERVICES: ServicePageContent[] = [
  MANAGED_COMPLIANCE,
  EVIDENCE_OPERATIONS,
  AUDITOR_ROOM,
  CROSSWALK,
];
